// Fullscreen fragment shader: the whole landing visual is procedural.
// Faces are signed distance fields; the split is a smooth-min (metaball)
// union whose blend radius collapses as scroll progress advances, which
// produces the liquid neck, the snap, and the droplets.
// Shared GLSL (uniforms, noise, faces, glow, cosmos) used by both story
// versions; each version appends its own main().
export const OMNIU_GLSL = `
precision highp float;

uniform vec2  uRes;
uniform float uTime;
uniform float uP;     // smoothed scroll progress, 0..6 (1 unit = 1 viewport)
uniform float uVel;   // smoothed scroll velocity
uniform vec2  uMouse; // -1..1

#define PI 3.14159265

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x),
             mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}

float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  mat2 m = mat2(1.6, 1.2, -1.2, 1.6);
  for (int i = 0; i < 4; i++) { v += a * noise(p); p = m * p; a *= 0.5; }
  return v;
}

float smin(float a, float b, float k) {
  k = max(k, 1e-4);
  float h = clamp(0.5 + 0.5 * (b - a) / k, 0.0, 1.0);
  return mix(b, a, h) - k * h * (1.0 - h);
}

// Arc around +y with half-aperture given by sc = (sin, cos).
float sdArc(vec2 p, vec2 sc, float ra, float rb) {
  p.x = abs(p.x);
  return ((sc.y * p.x > sc.x * p.y) ? length(p - sc * ra) : abs(length(p) - ra)) - rb;
}

float sdSeg(vec2 p, vec2 a, vec2 b) {
  vec2 pa = p - a, ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}

// Face in unit-radius space. anx: worried brows + wobbling mouth.
// peace: eyes close into content arcs.
float features(vec2 q, float smileA, float blink, vec2 look, float anx, float peace) {
  vec2 e = q - look * 0.07 * (1.0 - peace);
  float b = max(blink, 0.08);
  vec2 eq = vec2(abs(e.x) - 0.3, (e.y - 0.2) / b);
  float dots = (length(eq) - 0.085 + 0.015 * anx) * b;
  float arcs = sdArc(vec2(abs(e.x) - 0.3, e.y - 0.13), vec2(sin(1.15), cos(1.15)), 0.11, 0.035);
  float eyes = mix(dots, arcs, peace);

  vec2 m = e - vec2(0.0, 0.06);
  m.y = -m.y;
  float smile = sdArc(m, vec2(sin(smileA), cos(smileA)), 0.48, 0.045);
  float mx = clamp(e.x, -0.28, 0.28);
  float wy = -0.3 + 0.045 * sin(mx * 26.0 + uTime * 7.0);
  float wavy = length(vec2(e.x - mx, e.y - wy)) - 0.04;
  float mouth = mix(smile, wavy, anx);

  float brow = sdSeg(vec2(abs(e.x), e.y), vec2(0.15, 0.5), vec2(0.42, 0.39)) - 0.035 + (1.0 - anx) * 2.0;
  return min(min(eyes, mouth), brow);
}

// Squash & stretch along a motion direction, returns unit-radius coords.
vec2 toLocal(vec2 p, vec2 c, float R, vec2 dir, float s) {
  vec2 d = p - c;
  vec2 perp = vec2(-dir.y, dir.x);
  vec2 a = vec2(dot(d, dir) / (1.0 + s), dot(d, perp) * (1.0 + s * 0.6));
  return (a.x * dir + a.y * perp) / R;
}

// Horizontal-ish motion streaks trailing behind a face.
float streaks(vec2 d, vec2 dir, float R, float seed) {
  vec2 perp = vec2(-dir.y, dir.x);
  float along = dot(d, -dir);
  float across = dot(d, perp);
  float rowH = R * 0.11;
  float row = floor(across / rowH);
  float h = hash(vec2(row, seed));
  float len = R * (0.3 + 1.1 * h);
  float start = R * (0.55 + 0.3 * hash(vec2(row, seed + 7.0)));
  float yc = (row + 0.5) * rowH;
  float thick = R * 0.02 * (0.5 + h);
  float line = 1.0 - smoothstep(0.0, thick, abs(across - yc));
  float seg = smoothstep(start, start + R * 0.05, along) * (1.0 - smoothstep(start + len * 0.6, start + len, along));
  float rowMask = step(0.35, h) * (1.0 - smoothstep(R * 0.4, R * 0.85, abs(across)));
  return line * seg * rowMask;
}

float glowLine(float ad, float R) {
  float w = R * 0.018;
  return (1.0 - smoothstep(w * 0.4, w, ad)) * 1.2  // hard core
       + exp(-ad / (R * 0.025)) * 0.8               // tight glow
       + exp(-ad / (R * 0.2)) * 0.14;               // faint bloom
}

float glowFeat(float d, float R) {
  return (1.0 - smoothstep(0.0, R * 0.012, d)) * 1.25 + exp(-max(d, 0.0) / (R * 0.025)) * 0.5;
}

// One layer of stars on a jittered grid; brightness thresholded by hash.
float starLayer(vec2 p, float scale, float density, float size) {
  vec2 gv = p * scale;
  vec2 id = floor(gv);
  vec2 fr = fract(gv) - 0.5;
  float h = hash(id);
  vec2 o = (vec2(hash(id + 3.1), hash(id + 7.7)) - 0.5) * 0.7;
  float d = length(fr - o);
  float tw = 0.65 + 0.35 * sin(uTime * (0.5 + h * 2.0) + h * 50.0);
  return step(1.0 - density, h) * tw * ((1.0 - smoothstep(0.0, size, d)) + 0.012 / (d * scale + 0.02) * 0.05);
}

// Rare bright stars with diffraction spikes.
float flareLayer(vec2 p, float scale) {
  vec2 gv = p * scale;
  vec2 id = floor(gv);
  vec2 fr = fract(gv) - 0.5;
  float h = hash(id + 11.0);
  vec2 o = (vec2(hash(id + 5.3), hash(id + 9.1)) - 0.5) * 0.6;
  vec2 d = fr - o;
  float core = exp(-length(d) * 60.0);
  float spikes = exp(-abs(d.x) * 220.0) * exp(-abs(d.y) * 9.0) + exp(-abs(d.y) * 220.0) * exp(-abs(d.x) * 9.0);
  float tw = 0.7 + 0.3 * sin(uTime * 1.3 + h * 30.0);
  return step(0.86, h) * (core * 1.5 + spikes * 0.6) * tw;
}

// Deep-space backdrop: warped nebula, a galactic band with dust lanes, stars.
vec3 cosmos(vec2 p, float t, float scroll) {
  vec2 q = p + vec2(0.0, scroll * 0.05);
  vec2 r = mat2(0.82, -0.57, 0.57, 0.82) * q;
  float bd = r.y + 0.12 + 0.18 * (fbm(r * 1.4 + 2.0) - 0.5);
  float band = exp(-bd * bd * 14.0);

  vec2 wq = q * 1.3 + (vec2(fbm(q * 1.8 + t * 0.008), fbm(q * 1.8 + 4.0 - t * 0.008)) - 0.5) * 1.6;
  float n1 = fbm(wq);
  float n2 = fbm(wq * 2.2 + 7.0);

  vec3 col = vec3(0.002, 0.002, 0.01);
  col += pow(n1, 4.0) * vec3(0.42, 0.10, 0.62) * 1.6;      // violet nebula
  col += pow(n2, 5.0) * vec3(0.04, 0.30, 0.70) * 2.0;      // blue gas
  col += pow(n1 * n2, 2.0) * vec3(0.9, 0.25, 0.6) * 0.5;   // hot pink cores
  col += band * (pow(n2, 2.0) * vec3(0.26, 0.22, 0.46) + vec3(0.02, 0.02, 0.045));
  col *= 1.0 - band * smoothstep(0.48, 0.72, fbm(r * 5.0 + 3.0)) * 0.75; // dust lanes

  vec3 starTint = mix(vec3(0.75, 0.82, 1.0), vec3(1.0, 0.86, 0.75), hash(floor(q * 60.0)));
  float s = starLayer(q + vec2(0.0, scroll * 0.02), 110.0, 0.10 + 0.25 * band, 0.07) * 0.6
          + starLayer(q + vec2(0.0, scroll * 0.05), 55.0, 0.08 + 0.12 * band, 0.06)
          + starLayer(q + vec2(0.0, scroll * 0.09), 22.0, 0.10, 0.05) * 1.3;
  col += starTint * s;
  col += vec3(0.85, 0.9, 1.0) * flareLayer(q + vec2(0.0, scroll * 0.12), 4.5);
  return col;
}

`;

// v1: the submind hub story.
export const OMNIU_FRAG = OMNIU_GLSL + `
void main() {
  vec2 p = (gl_FragCoord.xy - 0.5 * uRes) / uRes.y;
  vec2 p0 = p;
  float t = uTime;

  // As the explanation scrolls in, the scene shrinks and rises out of its way.
  float zk = smoothstep(5.6, 6.5, uP);
  p = (p - vec2(0.0, 0.18 * zk)) / (1.0 - 0.32 * zk);
  float aspect = uRes.x / uRes.y;
  float ax = clamp(aspect / 1.6, 0.3, 1.0);
  float R = 0.15 * clamp(aspect / 1.25, 0.5, 1.0);

  // How violently things are moving right now.
  float energy = 0.0;
  for (int i = 1; i <= 4; i++) {
    float ti = clamp(uP - float(i), 0.0, 1.0);
    energy += sin(PI * ti);
  }
  energy = clamp(energy + abs(uVel) * 0.6, 0.0, 1.5);

  // Ghost-style phasing: intermittent glitch bursts that tear scanlines.
  // Anxiety drains with each split; peace settles in at the end.
  float anx = 1.0 - smoothstep(0.6, 4.9, uP);
  float peace = smoothstep(4.7, 5.6, uP);
  float burst = step(mix(0.84, 0.55, anx), noise(vec2(t * 1.1, 7.0)));
  // Phasing is the anxiety: it fades out completely as peace arrives.
  float gA = burst * (noise(vec2(t * 22.0, 1.0)) - 0.5) * 2.0 * anx;
  float band = floor(p.y * 70.0);
  p.x += (hash(vec2(band, floor(t * 24.0))) - 0.5) * 0.04 * abs(gA)
       * step(0.6, hash(vec2(band * 0.3, floor(t * 12.0))));

  // Liquid domain warp; calm shimmer at rest, turbulent while splitting.
  vec2 w = vec2(fbm(p * 3.0 + vec2(0.0, t * 0.35)), fbm(p * 3.0 + vec2(5.2, -t * 0.3))) - 0.5;
  vec2 pw = p + w * (0.008 + 0.07 * energy);

  vec3 cCenter = vec3(0.22, 0.62, 1.0);
  vec3 cViolet = vec3(0.58, 0.32, 1.0);
  vec3 cMagenta = vec3(0.82, 0.30, 0.95);

  // The face we follow. Sits above the headline in the hero, then centers.
  float heroK = 1.0 - smoothstep(0.0, 1.0, uP);
  float endK = smoothstep(5.0, 5.8, uP);
  vec2 c = vec2(sin(t * 0.6) * 0.012 + anx * 0.0035 * sin(t * 47.0), (0.05 + (1.0 - ax) * 0.04) * heroK + 0.04 * endK + sin(t * 0.9) * 0.01);
  vec2 look = clamp(uMouse, -1.0, 1.0);

  float dC = length(pw - c) - R;
  float D = dC;
  float wC = 1.0 / pow(max(dC, 0.0) + 0.03, 2.0);
  vec3 colAcc = cCenter * wC;
  float aAcc = wC;
  float wSum = wC;

  float blinkC = max(smoothstep(0.0, 0.03, abs(fract(t * 0.19) - 0.5)), 0.1);
  vec3 feat = cCenter * glowFeat(features((pw - c) / R, 1.0, blinkC, look, anx, peace) * R, R);
  vec3 extra = vec3(0.0);

  vec3 links = vec3(0.0);
  for (int i = 1; i <= 4; i++) {
    float fi = float(i);
    float ti = clamp(uP - fi, 0.0, 1.0);
    if (ti <= 0.0) continue;

    float age = min(max(uP - fi - 1.0, 0.0), 3.0);
    float side = mod(fi, 2.0) == 1.0 ? -1.0 : 1.0;
    // Each submind buds straight off toward its own spot in the hub.
    vec2 target = i == 1 ? vec2(-0.52, 0.04) : (i == 2 ? vec2(0.52, 0.04) : (i == 3 ? vec2(-0.3, -0.2) : vec2(0.3, -0.2)));
    target.x *= ax;
    float et = smoothstep(0.0, 1.0, ti);
    vec2 g = c + target * et + vec2(sin(t * 0.5 + fi * 1.7), cos(t * 0.4 + fi * 2.3)) * 0.015 * et;
    // Subminds: your face, smaller, and they never fade (mirrors thoughts.js).
    float sg = mix(1.0, 0.72, smoothstep(0.2, 0.9, ti));
    float ga = 0.9 * smoothstep(0.0, 0.05, ti);

    // At the end all four settle into their own spots around us (mirrors thoughts.js).
    float settle = smoothstep(5.0, 5.9, uP);
    vec2 slot = i == 1 ? vec2(-0.52, 0.04) : (i == 2 ? vec2(0.52, 0.04) : (i == 3 ? vec2(-0.3, -0.2) : vec2(0.3, -0.2)));
    slot.x *= ax;
    g = mix(g, c + slot, settle);
    sg = mix(sg, 0.72, settle);
    ga = mix(ga, 0.9, settle);
    vec2 v = normalize(target);
    float stretch = sin(PI * ti) * 0.45;

    vec2 q = toLocal(pw, g, R * sg, v, stretch);
    float dG = (length(q) - 1.0) * R * sg;

    // Liquid bridge: the blend radius shrinks until the neck snaps.
    float k = R * 1.1 * (1.0 - smoothstep(0.15, 0.8, ti));
    D = smin(D, dG, k);

    // Droplets flung off the neck as it breaks.
    float s = smoothstep(0.45, 0.7, ti) * (1.0 - smoothstep(0.8, 1.0, ti));
    if (s > 0.001) {
      vec2 perp = vec2(-v.y, v.x);
      for (int j = 0; j < 4; j++) {
        float fj = float(j);
        float hj = hash(vec2(fj, fi));
        float f = 0.22 + 0.18 * fj + 0.04 * sin(t * 3.0 + fj);
        vec2 pos = mix(c, g, f) + perp * (hj - 0.5) * 0.12 * ti;
        float r = R * 0.1 * s * (0.5 + 0.8 * hj);
        D = smin(D, length(pw - pos) - r, R * 0.18 * s);
      }
    }

    float wG = 1.0 / pow(max(dG, 0.0) + 0.03, 2.0);
    vec3 colG = mix(cViolet, cMagenta, age / 3.0);

    // Once the split completes, a thread connects us to this version.
    vec2 ba = g - c, pa = pw - c;
    float hl = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    float dl = length(pa - ba * hl);
    float lp = 0.5 + 0.5 * smoothstep(0.5, 1.0, sin(hl * 26.0 - t * 4.0 + fi));
    links += mix(cCenter, colG, hl) * (exp(-dl / (R * 0.018)) * 0.9 + exp(-dl / (R * 0.12)) * 0.12)
           * lp * smoothstep(0.35, 0.7, ti) * ga;
    colAcc += colG * wG;
    aAcc += ga * wG;
    wSum += wG;

    float smileG = 0.75 + 0.5 * hash(vec2(fi, 3.0));
    float blinkG = max(smoothstep(0.0, 0.03, abs(fract(t * 0.17 + fi * 0.31) - 0.5)), 0.1);
    feat += colG * ga * glowFeat(features(q, smileG, blinkG, look, 0.0, 0.0) * R * sg, R);
    extra += colG * ga * streaks(pw - g, v, R * sg, fi) * sin(PI * ti) * 1.4;
  }

  // Keep motion streaks and threads out of the faces themselves.
  extra *= smoothstep(0.0, R * 0.08, D);
  links *= smoothstep(0.0, R * 0.05, D);

  // Other people in the far background (screen space, unaffected by the end
  // zoom). Each starts muddled, then splits into its own line of selves; at
  // the end faint threads join everyone into one community. Mirrors thoughts.js.
  vec3 crowd = vec3(0.0);
  vec2 pc = p0 + w * 0.012;
  float zkS = smoothstep(5.6, 6.5, uP);
  vec2 cS = c * (1.0 - 0.32 * zkS) + vec2(0.0, 0.18 * zkS);   // our face, in screen space
  float community = smoothstep(5.2, 6.0, uP);
  vec3 cb = mix(cViolet, cCenter, 0.4);
  for (int k = 0; k < 3; k++) {
    float fk = float(k);
    vec2 b = k == 0 ? vec2(-0.72, 0.24) : (k == 1 ? vec2(0.72, -0.3) : vec2(-0.62, -0.3));
    vec2 dir = k == 0 ? vec2(0.0, -1.0) : (k == 1 ? vec2(0.0, 1.0) : vec2(1.0, 0.0));
    b.x *= ax;
    b += vec2(sin(t * 0.3 + fk * 2.0), cos(t * 0.27 + fk)) * 0.008;
    float rb = R * 0.32;
    float anxK = 1.0 - smoothstep(1.3, 4.4 + 0.2 * fk, uP);

    float db = abs(length(pc - b) - rb);
    float fb = features((pc - b) / rb, 1.0, 1.0, vec2(0.0), anxK, 0.0) * rb;
    crowd += cb * (exp(-db / (rb * 0.05)) * 0.5 + exp(-db / (rb * 0.3)) * 0.1 + glowFeat(fb, rb) * 0.35);

    for (int j = 1; j <= 3; j++) {
      float fj = float(j);
      float sj = smoothstep(0.0, 1.0, clamp(uP - (1.5 + 0.9 * (fj - 1.0) + 0.25 * fk), 0.0, 1.0));
      if (sj <= 0.0) continue;
      // Their own subminds orbit them on short tethers.
      float oa = fj * 2.094 + fk * 1.3 + t * 0.2;
      vec2 cj = mix(b, b + vec2(cos(oa), sin(oa)) * rb * 2.1, sj);
      float rj = rb * 0.6;
      float dj = abs(length(pc - cj) - rj);
      float fjd = features((pc - cj) / rj, 0.9, 1.0, vec2(0.0), 0.0, 0.0) * rj;
      vec2 ba = cj - b, pa = pc - b;
      float dl = length(pa - ba * clamp(dot(pa, ba) / max(dot(ba, ba), 1e-6), 0.0, 1.0));
      crowd += mix(cb, cViolet, 0.5) * sj * (exp(-dj / (rj * 0.05)) * 0.45 + glowFeat(fjd, rj) * 0.3
             + exp(-dl / (rb * 0.04)) * 0.25 * smoothstep(0.85, 1.0, sj) * step(rb, length(pa)) * step(rj, length(pc - cj)));
    }

    // Community: a faint thread from us to each of them.
    vec2 ba = b - cS, pa = pc - cS;
    float hl = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    float dl = length(pa - ba * hl);
    float lp = 0.5 + 0.5 * smoothstep(0.5, 1.0, sin(hl * 30.0 - t * 3.0 + fk));
    crowd += mix(cCenter, cb, hl) * exp(-dl / 0.0025) * 0.35 * lp * community
           * step(rb * 1.05, length(pc - b)) * step(R * (1.0 - 0.32 * zkS) * 1.05, length(pc - cS));
  }
  crowd *= 0.42;

  vec3 col = colAcc / wSum;
  float a = aAcc / wSum;
  float ad = abs(D);

  // Neon outline of the whole liquid union, with a chromatic split along
  // the normal that widens while things are moving or glitching.
  float ca = R * (0.01 + 0.03 * energy + 0.03 * abs(gA));
  vec3 ring = (col * glowLine(ad, R)
      + vec3(0.95, 0.25, 0.85) * exp(-abs(D - ca) / (R * 0.012)) * 0.45
      + vec3(0.25, 0.9, 1.0) * exp(-abs(D + ca) / (R * 0.012)) * 0.45) * a
    + vec3(0.9, 0.95, 1.0) * (1.0 - smoothstep(0.0, R * 0.008, ad)) * 0.7 * a;

  // Phasing echoes of the followed face (cyan left, magenta right).
  float echoAmt = (0.3 + 0.7 * abs(gA)) * anx;
  vec2 e1 = c + vec2(-R * (0.16 + 0.5 * abs(gA)), 0.0);
  vec2 e2 = c + vec2(R * (0.12 + 0.4 * abs(gA)), gA * R * 0.1);
  float de1 = abs(length(pw - e1) - R);
  float de2 = abs(length(pw - e2) - R);
  extra += vec3(0.2, 0.75, 1.0) * exp(-de1 / (R * 0.05)) * 0.35 * echoAmt;
  extra += vec3(0.75, 0.3, 1.0) * exp(-de2 / (R * 0.05)) * 0.35 * echoAmt;
  extra += vec3(0.2, 0.75, 1.0) * glowFeat(features((pw - e1) / R, 1.0, blinkC, look, anx, peace) * R, R) * 0.25 * echoAmt;
  extra += mix(cCenter, cViolet, 0.5) * streaks(pw - c, vec2(gA > 0.0 ? 1.0 : -1.0, 0.0), R, floor(t * 6.0)) * abs(gA) * 1.5;
  extra += cCenter * streaks(pw - c, vec2(0.0, -1.0), R, 11.0) * clamp(abs(uVel) * 1.5, 0.0, 1.0);

  // Ethereal smoke hugging the shape, ripples like a liquid surface.
  float field = exp(-max(D, 0.0) / (R * 0.7));
  vec2 sp = p * 2.2 + vec2(t * 0.05, -t * 0.08) + w * 2.0;
  float sm = fbm(sp + fbm(sp * 1.7 - t * 0.1));
  float ridge = 1.0 - abs(2.0 * sm - 1.0);
  vec3 smoke = col * (pow(sm, 3.0) * 0.4 + pow(ridge, 10.0) * 1.2) * field * (0.6 + energy * 1.4) * a;
  float ripple = smoothstep(0.55, 1.0, sin(D * 70.0 + sm * 10.0 - t * 2.0));
  smoke += col * ripple * exp(-abs(D) / (R * 0.35)) * (0.08 + 0.35 * energy) * a;
  vec3 inner = col * 0.05 * (1.0 - smoothstep(-R * 0.6, 0.0, D));

  // Sparkles: sparse field, denser and brighter near the spirit.
  vec2 gv = (p0 + vec2(0.0, t * 0.012)) * 38.0;
  vec2 id = floor(gv);
  vec2 fr = fract(gv) - 0.5;
  float h = hash(id);
  vec2 o = (vec2(hash(id + 3.1), hash(id + 7.7)) - 0.5) * 0.7;
  float tw = 0.5 + 0.5 * sin(t * (1.0 + h * 3.0) + h * 40.0);
  float star = (1.0 - smoothstep(0.0, 0.06, length(fr - o))) * step(0.86, h) * tw;
  vec3 stars = mix(vec3(0.6, 0.7, 1.0), col, field) * star * 1.8 * field * (0.5 + energy);

  // Darken the cosmos behind the spirit so it stays the brightest thing.
  vec3 bg = cosmos(p0, t, uP) * (1.0 - 0.75 * exp(-max(D, 0.0) / (R * 0.9)));

  vec3 outc = bg + crowd + links + inner + smoke + ring + feat + extra + stars;
  outc *= 1.0 - 0.35 * dot(p0 * 0.8, p0 * 0.8);
  outc = 1.0 - exp(-outc * 1.5);
  outc += (hash(gl_FragCoord.xy + t) - 0.5) / 255.0;
  gl_FragColor = vec4(outc, 1.0);
}
`;
